import { useEffect, useRef } from 'react'
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import * as S from './EditorTextBoxModal.styled'

export type EditorTextBoxModalTestId = `data-${string}`

export type EditorTextBoxModalAttrs = Partial<
  Record<EditorTextBoxModalTestId, true | string>
>

export type EditorTextBoxModalShellProps = {
  backdropTestId: EditorTextBoxModalTestId
  onDismiss: () => void
  onEscape?: () => void
  children: ReactNode
}

export const EditorTextBoxModalShell = ({
  backdropTestId,
  onDismiss,
  onEscape = onDismiss,
  children,
}: EditorTextBoxModalShellProps) => {
  const backdropPressRef = useRef(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // A style-bar popover open inside the dialog takes this Escape and
      // closes itself; the dialog closes on the next one.
      if (document.querySelector('[data-editor-text-sb-menu]')) return
      e.preventDefault()
      e.stopPropagation()
      onEscape()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  })

  const guardMouseDown = (e: ReactMouseEvent<HTMLDivElement>) => {
    const tag = e.target instanceof Element ? e.target.tagName : undefined
    if (tag !== 'INPUT' && tag !== 'TEXTAREA') e.preventDefault()
  }

  const dialog = (
    <S.Backdrop
      {...{ [backdropTestId]: true }}
      onMouseDown={(e) => {
        backdropPressRef.current = e.target === e.currentTarget
        guardMouseDown(e)
      }}
      onClick={(e) => {
        const pressed = backdropPressRef.current
        backdropPressRef.current = false
        if (pressed && e.target === e.currentTarget) onDismiss()
      }}
    >
      {children}
    </S.Backdrop>
  )

  return typeof document !== 'undefined'
    ? createPortal(dialog, document.body)
    : dialog
}

export default EditorTextBoxModalShell
